import { useState, useEffect, useCallback } from "react";
import type { Todo } from "@/types";
import { dbGet, dbSet } from "@/lib/db";
import { SK } from "@/lib/constants";

export function useTodos() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dbGet<Todo[]>(SK.TODOS).then((t) => {
      setTodos(t ?? []);
      setLoading(false);
    });
  }, []);

  const persist = useCallback((next: Todo[]) => {
    setTodos(next);
    dbSet(SK.TODOS, next);
  }, []);

  const addTodo = useCallback(
    (todo: Omit<Todo, "id" | "createdAt" | "done">) => {
      const newTodo: Todo = {
        ...todo,
        id: "T" + Date.now(),
        createdAt: new Date().toISOString(),
        done: false,
      };
      persist([...todos, newTodo]);
      return newTodo;
    },
    [todos, persist]
  );

  const toggleTodo = useCallback(
    (id: string) => {
      persist(todos.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
    },
    [todos, persist]
  );

  const deleteTodo = useCallback(
    (id: string) => {
      persist(todos.filter((t) => t.id !== id));
    },
    [todos, persist]
  );

  const clearAll = useCallback(() => {
    persist([]);
  }, [persist]);

  const setAllTodos = useCallback(
    (t: Todo[]) => persist(t),
    [persist]
  );

  return { todos, loading, addTodo, toggleTodo, deleteTodo, clearAll, setAllTodos };
}
